import RegisteredUserService from "../services/RegisteredUserService";
import * as registeredUserActions from "../constants/registeredUserActions";
import { checkVersionAndReload } from "../versionCheck";

export const registerUser = (username, rewardsId, dob, ticketNum, zipcode, password) => async (dispatch) => {
  try {
    dispatch({
      type: registeredUserActions.REGISTER_USER_REQUEST,
    });
    const response = await RegisteredUserService.registerUser(username, rewardsId, dob, ticketNum, zipcode, password);

    if (response.success) {
      dispatch({
        type: registeredUserActions.REGISTER_USER_SUCCESS,
        payload: response.data,
      });
    } else {
      dispatch({
        type: registeredUserActions.REGISTER_USER_FAIL,
        payload: response?.error,
      });
    }
  } catch (error) {
    dispatch({
      type: registeredUserActions.REGISTER_USER_FAIL,
      payload: (error.response && error.response.data) || error.message || error.toString(),
    });
  }
};

export const registeredLoginRequest =
  (username, password) => async (dispatch) => {

    // Check if application reload is needed
    const reloaded = await checkVersionAndReload();

    // If reload is executed, do not continue login. Page is refreshing.
    if (reloaded) {
      return;
    }

    // First log in and authenticate
    try {
      dispatch({
        type: registeredUserActions.REGISTERED_LOGIN_REQUEST,
      });

      const result = await RegisteredUserService.registeredLogin(username, password);

      if (result.success) {
        dispatch({
          type: registeredUserActions.REGISTERED_LOGIN_SUCCESS,
          payload: result.data,
        })
      } else {
        dispatch({
          type: registeredUserActions.REGISTERED_LOGIN_FAIL,
          payload: result.error,
        });
      }
    } catch (error) {
      const message =
        (error.response && error.response.data) ||
        error.message ||
        error.toString();
      dispatch({
        type: registeredUserActions.REGISTERED_LOGIN_FAIL,
        payload: message,
      });
    }
  };

export const registeredLogout = () => async (dispatch) => {

  // First log in and authenticate
  try {
    // Update the Redux state to reflect the logout action
    dispatch({
      type: registeredUserActions.REGISTERED_LOGOUT,
    });

    const result = await RegisteredUserService.logout();

  } catch (error) {
    const message =
      (error.response && error.response.data) ||
      error.message ||
      error.toString();
  } finally {
    // Clear the user data from local and session storage
    sessionStorage.removeItem("registeredUserState");
    localStorage.removeItem("TokenInterceptor");
  }

};


// Validate persisted registered session on app boot.
// If sessionStorage contains userData with rewardsId, first mark user as logged in,
// then call server to refresh/validate the dashboard. If server rejects, perform registeredLogout.
export const validateRegisteredSession = () => async (dispatch) => {
  try {
    const serialized = sessionStorage.getItem("registeredUserState");
    if (!serialized) {
      // nothing to validate
      return;
    }

    const persisted = JSON.parse(serialized);
    const persistedUserData = persisted && persisted.userData ? persisted.userData : null;

    if (!persistedUserData || !persistedUserData.rewardsId) {
      // no persisted logged-in user
      return;
    }

    // First mark the user as logged in (so route guards render)
    dispatch({
      type: registeredUserActions.REGISTERED_LOGIN_SUCCESS,
      payload: {
        firstName: persistedUserData.firstName,
        lastName: persistedUserData.lastName,
        rewardsId: persistedUserData.rewardsId,
      },
    });

    // Attempt to refresh the dashboard using the server cookie (withCredentials:true)
    // RegisteredUserService.registeredGetDashboard returns { success, data }.
    const response = await RegisteredUserService.registeredGetDashboard(persistedUserData.rewardsId);

    if (response && response.success) {
      // Replace dashboard state with server-provided fresh data
      dispatch({
        type: registeredUserActions.REGISTERED_GET_DASHBOARD_SUCCESS,
        payload: response.data,
      });
    } else {
      // Validation failed; force logout and clear persisted session
      await dispatch(registeredLogout());
    }
  } catch (err) {
    // On any error, do the safest thing and log out
    try {
      await dispatch(registeredLogout());
    } catch (e) {
      // ignore
    }
  }
};



export const regClaimOffer =
  (pageNumber = 0, pageSize = process.env.REACT_APP_DEFAULT_PAGE_SIZE) => async (dispatch, getState) => {

    // Retrieve the rewards ID from the redux state
    const rewardsId = getState().registeredUserDetails.userData.rewardsId;

    // Set the claim request state to requesting
    dispatch({
      type: registeredUserActions.REGISTERED_CLAIM_REQUEST,
    })

    try {
      // Call the service to claim the offer
      const response = await RegisteredUserService
        .registeredClaimRequest(rewardsId, pageNumber, pageSize);

      if (response.success) {
        dispatch({
          type: registeredUserActions.REGISTERED_CLAIM_SUCCESS,
          payload: response.data,
        });
      } else {
        dispatch({
          type: registeredUserActions.REGISTERED_CLAIM_FAIL,
          payload: response.error,
        });
      }

    } catch (error) {
      // Handle error and dispatch fail action
      const message =
        (error.response && error.response.data) ||
        error.message ||
        error.toString();

      dispatch({
        type: registeredUserActions.REGISTERED_CLAIM_FAIL,
        payload: message,
      });
    }
};

export const getRegClaimedOffers =
  (pageNumber = 0, pageSize = process.env.REACT_APP_DEFAULT_PAGE_SIZE) => async (dispatch, getState) => {

    const {
      registeredUserDetails: {userData},
    } = getState();

    // Set the claim request state to requesting
    dispatch({
      type: registeredUserActions.REGISTERED_GET_CLAIMED_REQUEST,
    })

    try {
      // Call the service to get the claimed modern offers
      const response = await RegisteredUserService
        .getClaimedOffers(userData.rewardsId, pageNumber, pageSize);

      if (response.success) {
        dispatch({
          type: registeredUserActions.REGISTERED_GET_CLAIMED_SUCCESS,
          payload: response.data,
        });
      } else {
        dispatch({
          type: registeredUserActions.REGISTERED_GET_CLAIMED_FAIL,
          payload: response.error,
        });
      }

    } catch (error) {
      // Handle error and dispatch fail action
      const message =
        (error.response && error.response.data) ||
        error.message ||
        error.toString();

      dispatch({
        type: registeredUserActions.REGISTERED_GET_CLAIMED_FAIL,
        payload: message,
      });
    }
};

export const getRegClaimedLegacyOffers =
  (pageNumber = 0, pageSize = process.env.REACT_APP_DEFAULT_PAGE_SIZE) => async (dispatch, getState) => {

    const {
      registeredUserDetails: {userData},
    } = getState();

    // Set the claim request state to requesting
    dispatch({
      type: registeredUserActions.REGISTERED_GET_CLAIMED_LEGACY_REQUEST,
    })

    try {
      // Call the service get the claimed legacy offers
      const response = await RegisteredUserService
        .getClaimedLegacyOffers(userData.rewardsId, pageNumber, pageSize);

      if (response.success) {
        dispatch({
          type: registeredUserActions.REGISTERED_GET_CLAIMED_LEGACY_SUCCESS,
          payload: response.data,
        });
      } else {
        dispatch({
          type: registeredUserActions.REGISTERED_GET_CLAIMED_LEGACY_FAIL,
          payload: response.error,
        });
      }

    } catch (error) {
      // Handle error and dispatch fail action
      const message =
        (error.response && error.response.data) ||
        error.message ||
        error.toString();

      dispatch({
        type: registeredUserActions.REGISTERED_GET_CLAIMED_LEGACY_FAIL,
        payload: message,
      });
    }
};

export const getDashboard =
  (pageNumber = 0, pageSize = process.env.REACT_APP_DEFAULT_PAGE_SIZE) => async (dispatch, getState) => {

    // Set the claim request state to requesting
    dispatch({
      type: registeredUserActions.REGISTERED_GET_DASHBOARD_REQUEST,
    })

    // Retrieve the rewards ID from the redux state
    const rewardsId = getState().registeredUserDetails.userData.rewardsId;

    try {
      // Call the service to get the claimed modern offers
      const response = await RegisteredUserService
        .registeredGetDashboard(rewardsId, pageNumber, pageSize);

      if (response.success) {
        dispatch({
          type: registeredUserActions.REGISTERED_GET_DASHBOARD_SUCCESS,
          payload: response.data,
        });
      } else {
        dispatch({
          type: registeredUserActions.REGISTERED_GET_DASHBOARD_FAIL,
          payload: response.error,
        });
      }

    } catch (error) {
      // Handle error and dispatch fail action
      const message =
        (error.response && error.response.data) ||
        error.message ||
        error.toString();

      dispatch({
        type: registeredUserActions.REGISTERED_GET_DASHBOARD_FAIL,
        payload: message,
      });
    }
};


export const resetPasswordRequest = (email) => async (dispatch) => {

  try {
    dispatch({
      type: registeredUserActions.RESET_PASSWORD_REQUEST,
    });
    const response = await RegisteredUserService.resetPasswordRequest(email);

    if (response.success) {
      dispatch({
        type: registeredUserActions.RESET_PASSWORD_SUCCESS,
        payload: response.data,
      });
    } else {
      dispatch({
        type: registeredUserActions.RESET_PASSWORD_FAIL,
        payload: response?.error,
      });
    }

    return response;

  } catch (error) {
    dispatch({
      type: registeredUserActions.RESET_PASSWORD_FAIL,
      payload: (error.response && error.response.data) || error.message || error.toString(),
    });
    return { success: false, error: error };
  }
};


export const resetPasswordExecute = (password, code) => async (dispatch) => {

  try {
    dispatch({
      type: registeredUserActions.RESET_PASSWORD_EXECUTE_REQUEST,
    });
    const response = await RegisteredUserService.resetPasswordExecute(password, code);

    if (response.success) {
      dispatch({
        type: registeredUserActions.RESET_PASSWORD_EXECUTE_SUCCESS,
        payload: response.data,
      });
    } else {
      dispatch({
        type: registeredUserActions.RESET_PASSWORD_EXECUTE_FAIL,
        payload: response?.error,
      });
    }

    return response;
  } catch (error) {
    dispatch({
      type: registeredUserActions.RESET_PASSWORD_EXECUTE_FAIL,
      payload: (error.response && error.response.data) || error.message || error.toString(),
    });
    return { success: false, error: error };
  }
};